import React, { useMemo } from "react";
import { getMacroDetails } from "../utils/energyCalculation";

const MACRO_CONFIG = [
  {
    id: "protein",
    nutrientNames: ["protein", "proteiner"],
    caloriesPerGram: 4,
    color: "#2563eb",
    translationKey: "Protein",
  },
  {
    id: "carbs",
    nutrientNames: ["kolhydrater", "carbohydrates", "carb", "kolhydrat"],
    caloriesPerGram: 4,
    color: "#22c55e",
    translationKey: "Kolhydrater",
  },
  {
    id: "fiber",
    nutrientNames: ["fibrer", "fiber", "kostfiber", "kostfibrer"],
    caloriesPerGram: 2,
    color: "#14b8a6",
    translationKey: "Fibrer",
  },
  {
    id: "fat",
    nutrientNames: ["fett", "fat"],
    caloriesPerGram: 9,
    color: "#f97316",
    translationKey: "Fett",
  },
];

const DishEnergyDistributionChart = ({ nutrients, t }) => {
  const { totalEnergy, segments } = useMemo(() => {
    // Use shared utility to get macro details and calculate energy from final macro values
    const { macros, totalEnergy: calculatedTotalEnergy } = getMacroDetails(nutrients, false);

    // Map macro details to segments with colors and translations
    const segmentsData = macros.map((macro) => {
      const config = MACRO_CONFIG.find((c) => c.id === macro.id);
      return {
        ...macro,
        color: config?.color || "#666",
        translationKey: config?.translationKey || macro.id,
      };
    });

    // Use calculated energy from macros; center label uses Math.ceil to match
    // ItemPage / product "Näringsvärden" (integer kcal via ceil, not round).
    const total = calculatedTotalEnergy;

    const segmentsWithPercentages = segmentsData.map((segment) => {
      const percentage = total > 0 ? (segment.energy / total) * 100 : 0;
      return {
        ...segment,
        percentage,
      };
    });

    return {
      totalEnergy: total,
      segments: segmentsWithPercentages,
    };
  }, [nutrients]);

  if (!segments || segments.length === 0 || totalEnergy <= 0) {
    return null;
  }

  let cumulativePercentage = 0;

  return (
    <div className="mt-8">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        {t("Energifördelning")} (per 100g)
      </h3>
      <div className="flex flex-col gap-6">
        <div className="relative w-48 h-48 mx-auto">
          <svg className="w-full h-full" viewBox="0 0 36 36">
            {segments.map((segment) => {
              const strokeDasharray = `${segment.percentage} ${100 - segment.percentage}`;
              const strokeDashoffset = 100 - cumulativePercentage;
              cumulativePercentage += segment.percentage;

              return (
                <circle
                  key={segment.id}
                  cx="18"
                  cy="18"
                  r="15.5"
                  fill="transparent"
                  stroke={segment.color}
                  strokeWidth="5"
                  strokeDasharray={strokeDasharray}
                  strokeDashoffset={strokeDashoffset}
                  strokeLinecap="round"
                  transform="rotate(-90 18 18)"
                />
              );
            })}
          </svg>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center">
            <span className="text-xs uppercase tracking-wide text-slate-400">
              {t("Energi")}
            </span>
            <span className="text-xl font-semibold text-slate-800">
              {Math.ceil(totalEnergy)} <span className="text-sm text-slate-500">kcal</span>
            </span>
            <span className="text-[11px] text-slate-400">
              {t("totalt")}
            </span>
          </div>
        </div>
        <div className="flex flex-col space-y-4">
          {segments.map((segment) => {
            const percentage = Math.round(segment.percentage);
            const energyDisplay = Math.ceil(segment.energy);
            return (
              <div
                key={segment.id}
                className="flex items-center justify-between gap-4 bg-white/60 border border-slate-200 rounded-lg px-3 py-2"
              >
                <div className="flex items-center gap-3">
                  <span
                    className="inline-block w-3 h-3 rounded-sm shadow-sm"
                    style={{ backgroundColor: segment.color }}
                  />
                  <div>
                    <p className="text-sm font-semibold text-gray-900">
                      {t(segment.translationKey)}
                    </p>
                    <p className="text-xs text-gray-500">
                      {segment.grams.toFixed(1)} g • {energyDisplay} kcal
                    </p>
                    <div className="mt-1 h-1.5 w-32 bg-slate-100 rounded-full overflow-hidden">
                      <div
                        className="h-full rounded-full"
                        style={{
                          width: `${Math.min(100, Math.max(0, percentage))}%`,
                          backgroundColor: segment.color,
                          opacity: 0.7,
                        }}
                      />
                    </div>
                  </div>
                </div>
                <span className="text-sm font-medium text-gray-700">
                  {percentage}%
                </span>
              </div>
            );
          })}
        </div>
      </div>
      <p className="text-xs text-gray-500 mt-3">
        {t(
          "Beräkning baserad på energi per 100 g. Protein och kolhydrater ger 4 kcal/g, fibrer 2 kcal/g och fett 9 kcal/g."
        )}
      </p>
    </div>
  );
};

export default DishEnergyDistributionChart;

